/**
 * Tempco UI Components Library
 * Brand-compliant React components following Tempco Brand Book 2023
 */

import React from 'react';
import { 
  Button, 
  Card, 
  Typography, 
  Alert,
  Box,
  TextField,
  Chip,
  TypographyProps
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled Components following Tempco Brand Guidelines

export const TempcoButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #ED1C24 30%, #F05259 90%)',
  border: 'none',
  borderRadius: 8,
  color: '#FFFFFF',
  fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
  fontWeight: 600,
  padding: '12px 24px',
  textTransform: 'none',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  
  '&:hover': {
    background: 'linear-gradient(45deg, #AD1219 30%, #ED1C24 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
  },
  
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  
  '&:disabled': {
    background: 'rgba(51, 51, 51, 0.5)',
    color: 'rgba(255, 255, 255, 0.3)',
    transform: 'none',
    boxShadow: 'none',
  },
}));

export const TempcoCard = styled(Card)(({ theme }) => ({
  background: 'rgba(68, 68, 68, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(237, 28, 36, 0.2)',
  borderRadius: 12,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    borderColor: 'rgba(237, 28, 36, 0.4)',
    boxShadow: '0 12px 40px rgba(237, 28, 36, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

export const TempcoTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
    
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      transition: 'all 0.2s ease-in-out',
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(237, 28, 36, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: '#ED1C24',
      borderWidth: 2,
    },
  },
  
  '& .MuiInputLabel-root': {
    fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
    
    '&.Mui-focused': {
      color: '#ED1C24',
    },
  },
}));

export const TempcoHeading = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontFamily: '"Oswald", "Arial Black", "Arial", sans-serif',
  fontWeight: 600,
  color: '#FFFFFF',
  letterSpacing: '-0.02em',
}));

export const TempcoBodyText = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
  color: '#FFFFFF',
  lineHeight: 1.6,
}));



// Component Props Interfaces
interface TempcoAlertProps {
  severity: 'success' | 'warning' | 'error' | 'info';
  children: React.ReactNode;
  onClose?: () => void;
}


interface TempcoGradientTextProps extends Omit<TypographyProps, 'variant'> {
  children: React.ReactNode;
  component?: React.ElementType;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
}

interface TempcoStatusChipProps {
  status: 'active' | 'pending' | 'complete' | 'error' | 'shipped' | 'processing';
  label: string;
}

// Functional Components

export const TempcoAlert: React.FC<TempcoAlertProps> = ({ 
  severity, 
  children, 
  onClose 
}) => {
  const getAlertStyles = (severity: string) => {
    const baseStyles = {
      borderRadius: 8,
      fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    };

    switch (severity) {
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          border: '1px solid rgba(76, 175, 80, 0.5)',
          color: '#81c784',
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          border: '1px solid rgba(255, 152, 0, 0.5)',
          color: '#ffb74d',
        };
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(237, 28, 36, 0.15)',
          border: '1px solid rgba(237, 28, 36, 0.5)',
          color: '#F05259',
        };
      case 'info':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(33, 150, 243, 0.15)',
          border: '1px solid rgba(33, 150, 243, 0.5)',
          color: '#64b5f6',
        };
      default:
        return baseStyles;
    }
  };

  return (
    <Alert 
      severity={severity} 
      onClose={onClose}
      sx={getAlertStyles(severity)}
    >
      {children}
    </Alert>
  );
};


export const TempcoGradientText: React.FC<TempcoGradientTextProps> = ({ 
  children, 
  component = 'span',
  variant = 'body1',
  sx,
  ...otherProps
}) => {
  return (
    <Typography
      component={component}
      variant={variant}
      sx={{
        background: 'linear-gradient(45deg, #ED1C24 30%, #F05259 90%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 600,
        fontFamily: variant.startsWith('h') 
          ? '"Oswald", "Arial Black", "Arial", sans-serif'
          : '"Lato", "Arial", "Helvetica", sans-serif',
        ...sx, // Allow custom sx styles to override defaults
      }}
      {...otherProps}
    >
      {children}
    </Typography>
  );
};

export const TempcoStatusChip: React.FC<TempcoStatusChipProps> = ({ 
  status, 
  label 
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'shipped':
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.2)',
          color: '#81c784',
          border: '1px solid rgba(76, 175, 80, 0.5)',
        };
      case 'pending':
      case 'processing':
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.2)',
          color: '#ffb74d',
          border: '1px solid rgba(255, 152, 0, 0.5)',
        };
      case 'complete':
        return {
          backgroundColor: 'rgba(237, 28, 36, 0.2)',
          color: '#F05259',
          border: '1px solid rgba(237, 28, 36, 0.5)',
        };
      case 'error':
        return {
          backgroundColor: 'rgba(244, 67, 54, 0.2)',
          color: '#ef5350',
          border: '1px solid rgba(244, 67, 54, 0.5)',
        };
      default:
        return {
          backgroundColor: 'rgba(237, 28, 36, 0.1)',
          color: '#FFFFFF',
          border: '1px solid rgba(237, 28, 36, 0.3)',
        };
    }
  };

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
        fontWeight: 500,
        ...getStatusColor(status),
      }}
    />
  );
};

// Container Components
export const TempcoContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  borderRadius: 12,
  background: 'rgba(68, 68, 68, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '3px dashed rgba(237, 28, 36, 0.4)',
}));



// Utility Functions

export const tempcoColors = {
  primary: '#ED1C24',
  secondary: '#AD1219',
  light: '#F05259',
  white: '#FFFFFF',
  black: '#000000',
  darkGray: '#333333',
  lightGray: '#D9D9D6',
};



